import React, { useEffect, useState } from 'react';
import { Event } from '../../../calendar/models/event';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import moment from 'moment';
import createNotification from '../../../utils/createNotification';
import Loader from 'react-loaders';
import { WHEREBY_ORIGIN } from '../../../utils/config';
import { Rnd } from 'react-rnd';
import { useStateSelector } from '../../../store/selectors';
import * as dates from 'date-arithmetic';

export interface ConferenceMeetingPopupProps {
    conference: {
        id: string | number;
        name: string;
        dateStart: Date;
        dateEnd: Date;
    };
    contextId: number;
    type: 'group' | 'marketplace' | 'marketplaceSupport';
    isAdminBuyerSupport?: boolean;
    onClose: (isConferenceClosed?: boolean) => void;
}
export const ConferenceMeetingPopup = (props: ConferenceMeetingPopupProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [conferenceLink, setConferenceLink] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [currentDate, setCurrentDate] = useState<Date>();
    const widthSizeSteps = [1366, 1024, 768, 480, 0];
    const heightSizeSteps = [768, 576, 512, 432, 0];
    const widthPercentages = [0.6, 0.75, 0.85, 0.9, 0.95];
    const heightPercentages = [0.71, 0.8, 0.85, 0.9, 0.95];
    const borderGap = 13;
    const getDefaultWidth = () => {
        const screenWidth = window.innerWidth;
        let indexOfWidth = widthSizeSteps.findIndex((s) => screenWidth >= s);
        if (indexOfWidth == -1) indexOfWidth = widthSizeSteps.length - 1;
        return widthPercentages[indexOfWidth] * screenWidth;
    };
    const getDefaultLeft = () => {
        const screenWidth = window.innerWidth;
        const popupWidth = getDefaultWidth();
        return (screenWidth - popupWidth) / 2;
    };
    const getDefaultHeight = () => {
        const screenHeight = window.innerHeight;
        let indexOfHeight = heightSizeSteps.findIndex((s) => screenHeight >= s);
        if (indexOfHeight == -1) indexOfHeight = heightSizeSteps.length - 1;
        const proposal = heightPercentages[indexOfHeight] * screenHeight;
        const defaultWidth = getDefaultWidth();
        return proposal > defaultWidth ? defaultWidth : proposal;
    };
    const getDefaultTop = () => {
        const screenHeight = window.innerHeight;
        const popupHeight = getDefaultHeight();
        return (screenHeight - popupHeight) / 2;
    };
    const [state, setState] = useState({
        width: getDefaultWidth() + 'px',
        height: getDefaultHeight() + 'px',
        x: getDefaultLeft(),
        y: getDefaultTop(),
    });

    const handleDragStop = (e: any, d: any) => {
        setIsDragging(false);
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        if (d.x < borderGap) d.x = borderGap;
        if (d.y < borderGap) d.y = borderGap;
        if (d.x > windowWidth) d.x = windowWidth - borderGap;
        if (d.y > windowWidth) d.y = windowWidth - borderGap;
        if (d.x > windowWidth - parseFloat(state.width) - borderGap) {
            d.x = windowWidth - parseFloat(state.width) - borderGap;
        }
        if (d.y > windowHeight - parseFloat(state.height) - borderGap) {
            d.y = windowHeight - parseFloat(state.height) - borderGap;
        }
        setState({ ...state, x: d.x, y: d.y });
    };

    const handleResizePopup = (
        e: any,
        direction: any,
        ref: any,
        delta: any,
        position: any
    ) => {
        let x = position.x;
        let y = position.y;
        let width = Number(ref.style.width.slice(0, -2));
        let height = Number(ref.style.height.slice(0, -2));
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        if (width < 300) width = 300;
        if (height < 300) height = 300;
        if (x < borderGap) x = borderGap;
        if (y < borderGap) y = borderGap;
        if (width > windowWidth - 2 * borderGap) {
            width = windowWidth - 2 * borderGap;
            x = borderGap;
        }
        if (height > windowHeight - 2 * borderGap) {
            height = windowHeight - 2 * borderGap;
            x = borderGap;
        }
        if (width + x > windowWidth - 2 * borderGap) {
            x = windowWidth - borderGap - width;
        }
        if (height + y > windowHeight - 2 * borderGap) {
            y = windowHeight - borderGap - height;
        }

        setState({
            x: x,
            y: y,
            width: width + 'px',
            height: height + 'px',
        });
    };

    function handleResizePage() {
        let x = state.x;
        let y = state.y;
        let height = Number(state.height.slice(0, -2));
        let width = Number(state.width.slice(0, -2));
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        if (width > windowWidth - 2 * borderGap) {
            width = windowWidth - 2 * borderGap;
            x = borderGap;
        }
        if (height > windowHeight - 2 * borderGap) {
            height = windowHeight - 2 * borderGap;
            y = borderGap;
        }
        if (x + width > windowWidth - 2 * borderGap) {
            x = (windowWidth - width) / 2;
        }
        if (y + height > windowHeight - 2 * borderGap) {
            y = (windowHeight - height) / 2;
        }
        setState({ x: x, y: y, height: height + 'px', width: width + 'px' });
    }

    useEffect(() => {
        setCurrentDate(new Date());
        const interval = setInterval(() => {
            setCurrentDate(new Date());
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        window.removeEventListener('resize', handleResizePage);
        window.addEventListener('resize', handleResizePage);
        return () => {
            window.removeEventListener('resize', handleResizePage);
        };
    }, [state]);

    useEffect(() => {
        if (
            !conferenceLink &&
            (currentDate > (!!props.isAdminBuyerSupport
                ? dates.add(new Date(props.conference.dateStart), -5, 'minutes') 
                : new Date(props.conference.dateStart))) &&
            !isLoading
        ) {
            setIsLoading(true);

            const url = props.type === 'group'
                ? `/api/groups/${props.contextId}/conferences/${props.conference.id}`
                : props.type === 'marketplaceSupport'
                    ? `/api/Marketplace/MarketplaceMeetings/${props.conference.id}`
                    : `/api/Marketplace/Engagements/${props.contextId}/EngagementMeetings/${props.conference.id}`;

            axios
                .get(url)
                .then((response: any) => {
                    if (response.status === 200) {
                        setConferenceLink(response.data);
                    } else if (response.response.status === 400) {
                        createNotification(
                            "The Meeting hasn't started yet. Please reload the page and try again.",
                            'error'
                        );
                    } else {
                        createNotification(
                            'Error occured while starting the Virtual Meetings',
                            'error'
                        );
                    }
                })
                .catch(() => {
                    createNotification(
                        'Error occured while starting the Virtual Meetings',
                        'error'
                    );
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [currentDate]);

    const handleIframeLoad = () => {
        window.addEventListener('message', function (event: MessageEvent) {
            if (event.origin === WHEREBY_ORIGIN) {
                if (event.data.type === 'leave') {
                    props.onClose(true);
                }
            }
        });
    };

    const style = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f0f0',
    } as const;

    return (
        <div>
            <Modal
                showMask={true}
                width={600}
                visible={true}
                animation={'slideRight'}
                isOpen={true}
                onClose={() => props.onClose(false)}
                closeMaskOnClick={false}
                className={
                    conferenceLink
                        ? 'conference-modal'
                        : 'conference-info-rodal'
                }
                center>
                {isLoading ? (
                    <div className="loading-container">
                        <Loader active={isLoading} type="ball-scale-multiple" />
                        <h5 className="mt-5">Loading the Meeting...</h5>
                    </div>
                ) : (
                    []
                )}
                {conferenceLink ? (
                    <Rnd
                        size={{ width: state.width, height: state.height }}
                        position={{ x: state.x, y: state.y }}
                        onDragStop={handleDragStop}
                        onDragStart={() => {
                            setIsDragging(true);
                        }}
                        onResizeStop={handleResizePopup}
                        className="conference-container"
                        style={style}>
                        <div className="drag-header">
                            <div>{props.conference.name} </div>
                            <div
                                className="close-rodal-button"
                                onClick={() => {
                                    props.onClose(false);
                                }}
                                onTouchEnd={() => {
                                    props.onClose(false);
                                }}>
                                <span className="rodal-close"></span>
                            </div>
                        </div>
                        {isDragging ? <div className="drag-panel"></div> : []}
                        <iframe
                            className="conference-iframe"
                            onLoad={handleIframeLoad}
                            src={conferenceLink}
                            allow="camera; microphone; fullscreen; speaker; display-capture; autoplay"></iframe>
                    </Rnd>
                ) : !isLoading ? (
                    <>
                        <ModalHeader>
                            <div>{props.conference.name} </div>
                            <div
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    props.onClose(false);
                                }}>
                                <span className="rodal-close"></span>
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            The meeting will begin at{' '}
                            {moment(props.conference.dateStart).format(
                                'h:mm A'
                            )}
                            .
                        </ModalBody>
                    </>
                ) : (
                    []
                )}
            </Modal>
        </div>
    );
};
